import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { MapPin, Phone, Globe, ArrowRight, List, Map as MapIcon, X, Navigation, Locate } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom red marker for rock bars
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Blue marker for user location
const blueIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Venue types for filtering
const VENUE_TYPES = [
  { id: "sala-conciertos", label: "Sala de Conciertos" },
  { id: "bar", label: "Bar" },
  { id: "cafe", label: "Café" },
  { id: "discoteca", label: "Discoteca" }
];

// Spain center (for country-wide view)
const SPAIN_CENTER = [40.4168, -3.7038]; // Madrid (center of Spain)
const SPAIN_ZOOM = 6;
const CITY_ZOOM = 12;

// Create custom cluster icon for cities
const createCityIcon = (count, cityName) => {
  return L.divIcon({
    className: 'city-cluster-icon',
    html: `
      <div style="
        background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
        color: white;
        border-radius: 50%;
        width: 50px;
        height: 50px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-family: monospace;
        font-weight: bold;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        border: 3px solid rgba(255,255,255,0.3);
        cursor: pointer;
      ">
        <span style="font-size: 16px; line-height: 1;">${count}</span>
        <span style="font-size: 8px; line-height: 1; opacity: 0.9;">bares</span>
      </div>
    `,
    iconSize: [50, 50],
    iconAnchor: [25, 25],
    popupAnchor: [0, -25]
  });
};

// Component to handle map view changes
function MapController({ center, zoom, fitBounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (fitBounds) {
      map.fitBounds(fitBounds, { padding: [50, 50] });
    } else if (center && zoom) {
      map.setView(center, zoom);
    }
  }, [center, zoom, fitBounds, map]);
  
  return null;
}

export default function Bars() {
  const [bars, setBars] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCity = searchParams.get("city") || "";
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"
  const [mapCenter, setMapCenter] = useState(SPAIN_CENTER);
  const [mapZoom, setMapZoom] = useState(SPAIN_ZOOM);
  const [userLocation, setUserLocation] = useState(null);
  const mapRef = useRef(null);

  // Group bars by city for cluster display
  const cityClusters = useMemo(() => {
    const barsWithLocation = bars.filter(b => b.latitude && b.longitude);
    const clusters = {};
    
    cities.forEach(city => {
      const cityBars = barsWithLocation.filter(b => b.city_id === city.city_id);
      if (cityBars.length > 0) {
        // Calculate center of all bars in this city
        const avgLat = cityBars.reduce((sum, b) => sum + b.latitude, 0) / cityBars.length;
        const avgLng = cityBars.reduce((sum, b) => sum + b.longitude, 0) / cityBars.length;
        
        clusters[city.city_id] = {
          city,
          bars: cityBars,
          count: cityBars.length,
          center: [avgLat, avgLng]
        };
      }
    });
    
    return clusters;
  }, [bars, cities]);

  // Get user's location (just show marker, no filtering)
  const requestUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lon: longitude });
          setMapCenter([latitude, longitude]);
          setMapZoom(CITY_ZOOM);
        },
        (error) => {
          console.log("Geolocation not available or denied:", error.message);
          alert("No se pudo obtener tu ubicación. Asegúrate de permitir el acceso a la ubicación.");
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
  };

  // Handle city selection from map cluster click
  const handleCityClick = (cityId) => {
    const params = new URLSearchParams(searchParams);
    params.set("city", cityId);
    setSearchParams(params);
  };

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await axios.get(`${API}/cities`);
        setCities(res.data);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    const fetchBars = async () => {
      setLoading(true);
      try {
        const params = {};
        if (selectedCity) params.city_id = selectedCity;
        
        // Add user location for proximity sorting
        if (userLocation) {
          params.lat = userLocation.lat;
          params.lon = userLocation.lon;
        }
        
        const res = await axios.get(`${API}/bars`, { params });
        setBars(res.data);
        
        // Map centering logic
        if (selectedCity) {
          // City selected: center on city
          const selectedCityData = cities.find(c => c.city_id === selectedCity);
          if (selectedCityData?.latitude && selectedCityData?.longitude) {
            setMapCenter([selectedCityData.latitude, selectedCityData.longitude]);
            setMapZoom(CITY_ZOOM);
          } else {
            // Fallback: center on bars in that city
            const cityBars = res.data.filter(b => b.latitude && b.longitude);
            if (cityBars.length > 0) {
              const avgLat = cityBars.reduce((sum, b) => sum + b.latitude, 0) / cityBars.length;
              const avgLng = cityBars.reduce((sum, b) => sum + b.longitude, 0) / cityBars.length;
              setMapCenter([avgLat, avgLng]);
              setMapZoom(CITY_ZOOM);
            }
          }
        } else if (!userLocation) {
          // No city and no user location: show Spain overview with city clusters
          setMapCenter(SPAIN_CENTER);
          setMapZoom(SPAIN_ZOOM);
        }
        
      } catch (error) {
        console.error("Error fetching bars:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBars();
  }, [selectedCity, cities]);

  const handleCityChange = (value) => {
    setUserLocation(null);
    if (value === "all") {
      searchParams.delete("city");
      setMapCenter(SPAIN_CENTER);
      setMapZoom(SPAIN_ZOOM);
    } else {
      searchParams.set("city", value);
    }
    setSearchParams(searchParams);
  };

  const barsWithLocation = bars.filter(b => b.latitude && b.longitude);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tighter">
              BARES <span className="text-primary">ROCKMAPS</span>
            </h1>
            <p className="font-mono text-sm text-zinc-500 mt-2">
              {bars.length} bar{bars.length !== 1 ? "es" : ""} 
              {barsWithLocation.length > 0 && ` · ${barsWithLocation.length} en el mapa`}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex bg-[#111] border border-zinc-800">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode("list")}
                className={`rounded-none font-mono text-xs px-4 ${
                  viewMode === "list" ? "bg-primary text-white" : "text-zinc-400 hover:text-white"
                }`}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4 mr-2" />
                LISTA
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode("map")}
                className={`rounded-none font-mono text-xs px-4 ${
                  viewMode === "map" ? "bg-primary text-white" : "text-zinc-400 hover:text-white"
                }`}
                data-testid="view-map-btn"
              >
                <MapIcon className="w-4 h-4 mr-2" />
                MAPA
              </Button>
            </div>

            {/* City Filter */}
            <Select value={selectedCity || "all"} onValueChange={handleCityChange}>
              <SelectTrigger 
                className="w-[200px] bg-[#111] border-zinc-800 font-mono text-sm rounded-none"
                data-testid="city-filter"
              >
                <SelectValue placeholder="Filtrar por ciudad" />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-zinc-800 rounded-none z-[9999]">
                <SelectItem value="all" className="font-mono text-sm rounded-none">
                  TODAS LAS CIUDADES
                </SelectItem>
                {cities.map((city) => (
                  <SelectItem 
                    key={city.city_id} 
                    value={city.city_id}
                    className="font-mono text-sm rounded-none"
                  >
                    {city.name.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* My Location Button */}
        {viewMode === "map" && (
          <div className="flex items-center gap-3 mb-6">
            {userLocation ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUserLocation(null)}
                className="border-blue-500 text-blue-400 hover:text-blue-300 hover:border-blue-400 font-mono text-xs"
                data-testid="clear-location-btn"
              >
                <Locate className="w-4 h-4 mr-2" />
                UBICACIÓN ACTIVA
                <X className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={requestUserLocation}
                className="border-zinc-700 text-zinc-300 hover:text-white hover:border-primary font-mono text-xs"
                data-testid="my-location-btn"
              >
                <Navigation className="w-4 h-4 mr-2" />
                MI UBICACIÓN
              </Button>
            )}
          </div>
        )}

        {/* Map View */}
        {viewMode === "map" && (
          <div className="mb-8">
            <div className="h-[600px] border border-zinc-800 overflow-hidden" data-testid="bars-map">
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
                ref={mapRef}
              >
                <MapController center={mapCenter} zoom={mapZoom} />
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                />
                
                {/* User location marker */}
                {userLocation && (
                  <Marker 
                    position={[userLocation.lat, userLocation.lon]}
                    icon={blueIcon}
                  >
                    <Popup>
                      <div className="text-center">
                        <p className="font-bold text-blue-600">TU UBICACIÓN</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {/* Show city clusters when no city is selected */}
                {!selectedCity && Object.values(cityClusters).map((cluster) => (
                  <Marker
                    key={cluster.city.city_id}
                    position={cluster.center}
                    icon={createCityIcon(cluster.count, cluster.city.name)}
                    eventHandlers={{
                      click: () => handleCityClick(cluster.city.city_id)
                    }}
                  >
                    <Popup>
                      <div className="text-center min-w-[150px]">
                        <h3 className="font-bold text-lg text-gray-900">{cluster.city.name}</h3>
                        <p className="text-sm text-gray-600 mb-2">{cluster.count} bares rock</p>
                        <button
                          onClick={() => handleCityClick(cluster.city.city_id)}
                          className="bg-red-600 text-white px-4 py-2 text-sm font-bold rounded w-full hover:bg-red-700"
                        >
                          VER BARES
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                
                {/* Show individual bar markers when city is selected */}
                {selectedCity && bars.filter(b => b.latitude && b.longitude).map((bar) => (
                  <Marker 
                    key={bar.bar_id} 
                    position={[bar.latitude, bar.longitude]}
                    icon={redIcon}
                  >
                    <Popup className="rock-popup">
                      <div className="min-w-[220px] max-w-[280px]">
                        {bar.image_url && (
                          <div className="w-full h-24 mb-2 overflow-hidden rounded">
                            <img 
                              src={bar.image_url} 
                              alt={bar.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        
                        <h3 className="font-bold text-lg mb-1 text-red-600">{bar.name}</h3>
                        
                        {bar.address && (
                          <p className="text-sm text-gray-600 mb-1">{bar.address}</p>
                        )}
                        
                        {bar.venue_type && (
                          <div className="mb-2">
                            <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded">
                              {VENUE_TYPES.find(t => t.id === bar.venue_type)?.label || bar.venue_type}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex gap-2 mt-2">
                          <Link
                            to={`/bars/${bar.bar_id}`}
                            className="flex-1 text-center py-2 px-3 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded"
                          >
                            VER PERFIL
                          </Link>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${bar.latitude},${bar.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-center py-2 px-3 text-sm font-bold text-gray-700 bg-gray-200 hover:bg-gray-300 rounded"
                          >
                            CÓMO LLEGAR
                          </a>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
            
            {/* City chips below map for quick navigation */}
            {!selectedCity && Object.keys(cityClusters).length > 0 && (
              <div className="mt-4">
                <p className="font-mono text-xs text-zinc-500 mb-2">CIUDADES CON BARES:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.values(cityClusters).sort((a, b) => b.count - a.count).map((cluster) => (
                    <button
                      key={cluster.city.city_id}
                      onClick={() => handleCityClick(cluster.city.city_id)}
                      className="bg-[#111] border border-zinc-800 px-4 py-2 font-mono text-sm text-zinc-300 hover:text-primary hover:border-primary transition-colors flex items-center gap-2"
                    >
                      <MapPin className="w-3 h-3 text-primary" />
                      {cluster.city.name}
                      <span className="bg-primary/20 text-primary px-2 py-0.5 text-xs">
                        {cluster.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Bar list below map when viewing a city */}
            {selectedCity && barsWithLocation.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {bars.filter(b => b.latitude && b.longitude).map((bar) => (
                  <Link
                    key={bar.bar_id}
                    to={`/bars/${bar.bar_id}`}
                    className="bg-[#111] border border-zinc-800 px-4 py-2 font-mono text-sm text-zinc-300 hover:text-primary hover:border-primary transition-colors"
                  >
                    <MapPin className="w-3 h-3 inline mr-2 text-primary" />
                    {bar.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* List View / Bars Grid */}
        {viewMode === "list" && (
          <>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-[#111] border border-zinc-800 h-80 animate-pulse" />
                ))}
              </div>
            ) : bars.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {bars.map((bar) => (
                  <Link
                    key={bar.bar_id}
                    to={`/bars/${bar.bar_id}`}
                    className="group card-ruido bg-[#111] border border-zinc-800 overflow-hidden"
                    data-testid={`bar-card-${bar.bar_id}`}
                  >
                    <div className="aspect-video relative overflow-hidden">
                      {bar.image_url ? (
                        <img
                          src={bar.image_url}
                          alt={bar.name}
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                        />
                      ) : (
                        <div 
                          className="w-full h-full bg-cover bg-center opacity-60"
                          style={{
                            backgroundImage: "url('https://images.unsplash.com/photo-1691986660255-4467453933be?crop=entropy&cs=srgb&fm=jpg&q=85')"
                          }}
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                      
                      {/* Location badge */}
                      {bar.latitude && bar.longitude && (
                        <div className="absolute top-2 right-2 bg-primary/90 text-white text-xs font-mono px-2 py-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          EN MAPA
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <h3 className="text-xl font-black text-white mb-2 tracking-tight group-hover:text-primary transition-colors">
                        {bar.name}
                      </h3>
                      
                      {bar.description && (
                        <p className="text-sm text-zinc-500 mb-4 line-clamp-2">
                          {bar.description}
                        </p>
                      )}

                      <div className="space-y-2">
                        {bar.address && (
                          <div className="flex items-center gap-2 text-zinc-400 font-mono text-xs">
                            <MapPin className="w-3 h-3 text-primary" />
                            <span className="truncate">{bar.address}</span>
                          </div>
                        )}
                        {bar.phone && bar.show_phone !== false && (
                          <div className="flex items-center gap-2 text-zinc-400 font-mono text-xs">
                            <Phone className="w-3 h-3 text-primary" />
                            <span>{bar.phone}</span>
                          </div>
                        )}
                        {bar.website && (
                          <div className="flex items-center gap-2 text-zinc-400 font-mono text-xs">
                            <Globe className="w-3 h-3 text-primary" />
                            <span className="truncate">{bar.website}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
                        <span className="font-mono text-xs text-primary">VER PERFIL</span>
                        <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="bg-[#111] border border-zinc-800 p-12 text-center">
                <MapPin className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">NO HAY BARES AÚN</h3>
                <p className="text-zinc-500 font-mono text-sm">
                  {selectedCity ? "No hay bares en esta ciudad todavía" : "Sé el primero en registrar tu bar"}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
